import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tables } from "@/types/database.types";
import { clearAllCaches } from "@/services/templateService";
import { checkPlanLimits } from "@/lib/planLimits";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

import FormHeader from "./form/FormHeader";
import BasicInfoTab from "./form/BasicInfoTab";
import InstructionsTab from "./form/InstructionsTab";
import NotificationsTab from "./form/NotificationsTab";
import FormActions from "./form/FormActions";
import { agentFormSchema, AgentFormValues } from "./form/AgentFormSchema";

type AgentFormProps = {
  agent?: Tables<"agents">;
  isEditMode?: boolean;
  hideSubmitButton?: boolean;
};

const AgentForm = ({ agent, isEditMode = false, hideSubmitButton = false }: AgentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planLimitError, setPlanLimitError] = useState<string | null>(null);
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const processConversationStarters = (starters: string | undefined | null): string[] => {
    if (!starters) return [];
    return starters.split('\n').filter(s => s.trim() !== '').map(s => s.trim());
  };

  const form = useForm<AgentFormValues>({
    resolver: zodResolver(agentFormSchema),
    defaultValues: isEditMode && agent
      ? {
        title: agent.title || "",
        description: agent.description || "",
        instructions: agent.instructions || "",
        restrictions: agent.restrictions || "",
        notification_email: agent.notification_email || "",
        is_active: agent.is_active !== undefined ? agent.is_active : true,
        welcome_message: agent.welcome_message || "",
        thank_you_message: agent.thank_you_message || "",
        agent_name: agent.agent_name || "",
        conversation_starters: agent.conversation_starters || [],
        banner: agent.banner || null,
      }
      : {
        title: "",
        description: "",
        instructions: "",
        restrictions: "",
        notification_email: "",
        is_active: true,
        welcome_message: "",
        thank_you_message: "",
        agent_name: "",
        conversation_starters: [],
        banner: null,
      },
  });

  const onSubmit = async (values: AgentFormValues) => {
    if (!user || !organization) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "You must be logged in with an organization to create agents.",
      });
      return;
    }

    setIsSubmitting(true);
    setPlanLimitError(null);

    try {
      // Check plan limits before proceeding (only for new agents)
      if (!isEditMode) {
        const limitError = await checkPlanLimits(organization.id, "active_agents");
        if (limitError) {
          setPlanLimitError(limitError.message);
          setIsSubmitting(false);
          return;
        }
      }

      if (isEditMode && agent) {
        const { error } = await supabase
          .from("agents")
          .update({
            title: values.title,
            description: values.description || null,
            instructions: values.instructions || null,
            restrictions: values.restrictions || null,
            notification_email: values.notification_email || null,
            is_active: values.is_active,
            welcome_message: values.welcome_message || null,
            thank_you_message: values.thank_you_message || null,
            agent_name: values.agent_name || null,
            conversation_starters: values.conversation_starters || [],
            banner: values.banner || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", agent.id);

        if (error) throw error;

        // Clear any cached agent data
        clearAllCaches();

        toast({
          title: "Agent updated",
          description: "The agent has been updated successfully.",
        });

        navigate(`/agents/${agent.id}`);
      } else {
        const { data, error } = await supabase
          .from("agents")
          .insert({
            title: values.title,
            description: values.description || null,
            instructions: values.instructions || null,
            restrictions: values.restrictions || null,
            notification_email: values.notification_email || null,
            is_active: values.is_active,
            welcome_message: values.welcome_message || null,
            thank_you_message: values.thank_you_message || null,
            agent_name: values.agent_name || null,
            conversation_starters: values.conversation_starters || [],
            banner: values.banner || null,
            organization_id: organization.id,
            created_by: user.id,
          })
          .select()
          .single();

        if (error) throw error;

        // Clear any cached agent data
        clearAllCaches();

        toast({
          title: "Agent created",
          description: "Your new agent has been created successfully.",
        });

        navigate(`/agents/${data.id}`);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred.";
      toast({
        variant: "destructive",
        title: "Error saving agent",
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <FormHeader title={isEditMode ? "Edit Agent" : "Create Agent"} />

      {planLimitError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Plan Limit Reached</AlertTitle>
          <AlertDescription>
            {planLimitError}
            <div className="mt-4">
              <Button onClick={() => navigate('/pricing')} variant="secondary">
                Upgrade Plan
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Tabs defaultValue="basic">
            <TabsList className="grid grid-cols-3 mb-8">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="instructions">Instructions</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <BasicInfoTab form={form} />
            </TabsContent>

            <TabsContent value="instructions">
              <InstructionsTab form={form} />
            </TabsContent>

            <TabsContent value="notifications">
              <NotificationsTab form={form} />
            </TabsContent>
          </Tabs>

          <FormActions
            isSubmitting={isSubmitting}
            isEditMode={isEditMode}
            hideSubmitButton={hideSubmitButton}
          />
        </form>
      </Form>
    </div>
  );
};

export default AgentForm;
